import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../contexts/useAuth'
import { apiFetch, ApiError } from '../../lib/api'
import { formatOfficerDate, humanizeToken } from '../../lib/officerFormat'
import DocumentLink from '../documents/DocumentLink'
import { ErrorState } from '../state'
import { subchainOverviewHref, type ContextWork, type ViewScope, type WorkCard } from './context'
import { useRead } from './useRead'
import type {
  ReportingClose,
  ReportingPeriod,
  ReportingReview,
  ReportingSchedule,
  ReportingSignoff,
} from './reportingTypes'
import './fund-reporting.css'

interface ScheduleRead {
  value?: ReportingSchedule
  loading: boolean
  error?: unknown
}
interface Props {
  fundId: string
  scope: ViewScope
  work?: ContextWork
  schedule: ScheduleRead
  refresh: number
  onRefresh: () => void
  editorOpen: boolean
  onEdit: () => void
  onCloseEditor: () => void
}

function periodLabel(period: Pick<ReportingPeriod, 'as_of' | 'due_date'>): string {
  const cutoff = period.as_of ? formatOfficerDate(period.as_of) : 'Financial cutoff not set'
  return cutoff + (period.due_date ? ` · due ${formatOfficerDate(period.due_date)}` : '')
}

function failure(error: unknown): string {
  if (
    error instanceof ApiError &&
    typeof error.body === 'object' &&
    error.body !== null &&
    'detail' in error.body &&
    typeof error.body.detail === 'string'
  )
    return error.body.detail
  return error instanceof Error ? error.message : 'The reporting information could not be saved.'
}

const blockerLabels: Record<string, string> = {
  reporting_cutoff_missing: 'Set the financial reporting cutoff.',
  reporting_cutoff_has_not_occurred: 'This financial reporting cutoff has not occurred yet.',
  fund_head_unavailable: 'The confirmed fund head is unavailable.',
  fund_scope_not_established: 'The reporting scope is not established.',
  reporting_schedule_requires_review: 'Review the reporting dates.',
  fund_agreement_inactive: 'The fund agreement is not active.',
  reporting_schedule_source_changed: 'The reporting schedule source has changed.',
  reporting_schedule_source_unavailable: 'The reporting schedule source is unavailable.',
  position_financial_date_unresolved: 'A position needs its financial date.',
  look_through_pending: 'A look-through decision is pending.',
  booking_pending: 'Record changes still need to be booked.',
  booking_affinity_unavailable: 'Review the affected booking work before sign-off.',
  booking_scope_unavailable: 'The booking check could not complete. Refresh it before sign-off.',
  subchain_not_closed: 'A subchain is not Closed for this reporting date.',
  dated_subchain_close_required: 'Close is required for this date.',
  subchain_current_context_unavailable: 'The current subchain context is unavailable.',
  earlier_close_open: 'An earlier period needs review first.',
  earlier_close_check_pending: 'An earlier period needs a fresh check first.',
  record_edit_pending: 'A Record edit needs approval.',
  material_change_financial_date_unknown: 'A changed Record fact needs its financial date.',
}

function Blockers({ values }: { values: string[] }) {
  const labels = [
    ...new Set(
      values.map(
        (value) => blockerLabels[value.split(':')[0]] ?? humanizeToken(value.split(':')[0])
      )
    ),
  ]
  return labels.length ? (
    <ul className="fr-blockers">
      {labels.map((label) => (
        <li key={label}>{label}</li>
      ))}
    </ul>
  ) : null
}

function Source({ period, fundId }: { period: ReportingPeriod; fundId: string }) {
  if (!period.source_document_id) return <small className="fr-muted">Entered by an officer</small>
  const source = period.source
  return (
    <div className="fr-source">
      <DocumentLink
        document={{
          document_id: period.source_document_id,
          display_title: source?.title,
          page: source?.schedule_claim?.page,
        }}
        context={{ fundId, tab: 'overview' }}
      />
      {source?.schedule_claim?.quote ? (
        <blockquote>{source.schedule_claim.quote}</blockquote>
      ) : null}
      {source?.schedule_claim?.recurrence_text ? (
        <small>{source.schedule_claim.recurrence_text}</small>
      ) : null}
      {source?.schedule_claim?.ambiguity ? <small>{source.schedule_claim.ambiguity}</small> : null}
      {source?.officer_values ? (
        <small>Dates reviewed by an officer; original source retained.</small>
      ) : null}
    </div>
  )
}

function Dialog({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  const id = useId()
  useEffect(() => {
    const active = document.activeElement
    const dialog = ref.current
    dialog?.showModal()
    return () => {
      dialog?.close()
      if (active instanceof HTMLElement && active.isConnected) active.focus()
    }
  }, [])
  return (
    <dialog
      ref={ref}
      className="nav-folder-dialog fr-dialog"
      aria-labelledby={id}
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
    >
      <header>
        <h2 id={id}>{title}</h2>
        <button type="button" className="nav-link" onClick={onClose} aria-label={`Close ${title}`}>
          Close ×
        </button>
      </header>
      {children}
    </dialog>
  )
}

export function ReportingDatesFact({ read, onEdit }: { read: ScheduleRead; onEdit: () => void }) {
  if (read.loading) return <>Loading…</>
  if (read.error || !read.value) return <>Reporting dates unavailable</>
  return (
    <>
      {read.value.periods.length ? (
        read.value.periods.map((period) => (
          <button className="nav-link fr-fact-date" key={period.id} onClick={onEdit}>
            {periodLabel(period)} ›
          </button>
        ))
      ) : (
        <button className="nav-link" onClick={onEdit}>
          Not set · Add dates ›
        </button>
      )}
      {read.value.periods.length ? (
        <button className="nav-link fr-fact-edit" onClick={onEdit}>
          Edit reporting dates ›
        </button>
      ) : null}
    </>
  )
}

interface DraftPeriod {
  key: string
  original?: ReportingPeriod
  asOf: string
  dueDate: string
}

function ScheduleEditor({
  schedule,
  manager,
  onClose,
  onSaved,
  onStale,
}: {
  schedule: ReportingSchedule
  manager: boolean
  onClose: () => void
  onSaved: () => void
  onStale: (message: string) => void
}) {
  const [rows, setRows] = useState<DraftPeriod[]>(() =>
    schedule.periods.length
      ? schedule.periods.map((period) => ({
          key: period.id,
          original: period,
          asOf: period.as_of ?? '',
          dueDate: period.due_date ?? '',
        }))
      : [{ key: crypto.randomUUID(), asOf: '', dueDate: '' }]
  )
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const pending = useRef(false)
  const retry = useRef<{ content: string; id: string } | null>(null)
  const changesSignedCutoff = schedule.periods.some(
    (period) =>
      period.decision_record_event_id &&
      rows.find((row) => row.original?.id === period.id)?.asOf !== (period.as_of ?? '')
  )
  const change = (key: string, field: 'asOf' | 'dueDate', value: string) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, [field]: value } : row)))
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (pending.current) return
    if (
      rows.some(
        (row) =>
          !row.asOf &&
          !row.dueDate &&
          !(
            row.original?.status === 'needs_review' &&
            !row.original.as_of &&
            !row.original.due_date
          )
      )
    ) {
      setError('Enter a financial cutoff or a due date for each row, or remove the row.')
      return
    }
    const cutoffs = rows.map((row) => row.asOf).filter(Boolean)
    if (new Set(cutoffs).size !== cutoffs.length) {
      setError('List each financial reporting cutoff once.')
      return
    }
    if (changesSignedCutoff && (!manager || !reason.trim())) {
      setError('A manager must explain a change to an already signed reporting cutoff.')
      return
    }
    const body = {
      schedule_revision: schedule.schedule_revision,
      periods: rows.map((row) => ({
        id: row.original?.id,
        as_of: row.asOf || null,
        due_date: row.dueDate || null,
        source_document_id: row.original?.source_document_id ?? null,
        source_binding: row.original?.source_binding ?? null,
      })),
      reason: reason.trim() || null,
    }
    const content = JSON.stringify(body)
    if (retry.current?.content !== content) retry.current = { content, id: crypto.randomUUID() }
    pending.current = true
    setSaving(true)
    setError(null)
    try {
      await apiFetch<ReportingSchedule>(`/api/fund-reporting/funds/${schedule.fund_id}/schedule`, {
        method: 'PUT',
        body: JSON.stringify({ ...body, idempotency_key: retry.current.id }),
      })
      onSaved()
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409)
        onStale(
          `${failure(caught)} The current schedule is being reloaded; review it before saving again.`
        )
      else setError(failure(caught))
    } finally {
      pending.current = false
      setSaving(false)
    }
  }
  return (
    <Dialog
      title="Reporting dates"
      onClose={() => {
        if (!pending.current) onClose()
      }}
    >
      <p>
        Record every required reporting date. The financial cutoff and the deadline for delivering
        the report are separate dates.
      </p>
      <form onSubmit={submit}>
        <div className="fr-date-rows">
          {rows.map((row, index) => {
            const protectedCutoff = !!row.original?.decision_record_event_id && !manager
            return (
              <fieldset className="fr-date-row" key={row.key} disabled={saving}>
                <legend>Reporting period {index + 1}</legend>
                <div className="fr-date-inputs">
                  <label>
                    Financial cutoff
                    <input
                      type="date"
                      aria-label={`Financial cutoff ${index + 1}`}
                      value={row.asOf}
                      disabled={protectedCutoff}
                      onChange={(event) => change(row.key, 'asOf', event.target.value)}
                    />
                  </label>
                  <label>
                    Report due date · optional
                    <input
                      type="date"
                      aria-label={`Report due date ${index + 1}`}
                      value={row.dueDate}
                      onChange={(event) => change(row.key, 'dueDate', event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="nav-link"
                    disabled={protectedCutoff}
                    aria-label={`Remove reporting period ${index + 1}`}
                    onClick={() =>
                      setRows((current) => current.filter((item) => item.key !== row.key))
                    }
                  >
                    Remove
                  </button>
                </div>
                {protectedCutoff ? (
                  <small className="fr-muted">
                    Only a manager can change a signed financial cutoff.
                  </small>
                ) : null}
                {row.original ? (
                  <Source period={row.original} fundId={schedule.fund_id} />
                ) : (
                  <small className="fr-muted">Officer-entered dates</small>
                )}
              </fieldset>
            )
          })}
        </div>
        <button
          type="button"
          className="nav-link"
          disabled={saving || rows.length >= 100}
          onClick={() =>
            setRows((current) => [...current, { key: crypto.randomUUID(), asOf: '', dueDate: '' }])
          }
        >
          Add another reporting period ›
        </button>
        <label className="fr-reason">
          Reason for change{changesSignedCutoff ? '' : ' · optional'}
          <textarea
            rows={2}
            maxLength={2000}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            disabled={saving}
            required={changesSignedCutoff}
          />
        </label>
        {!rows.length ? (
          <p className="fr-muted">
            Saving removes the active schedule. Previous decisions remain in history.
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="fr-error">
            {error}
          </p>
        ) : null}
        <div className="fr-actions">
          <button type="submit" className="fr-button primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save dates'}
          </button>
          <button type="button" className="fr-button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  )
}

function matchingCard(
  item: ReportingClose,
  work: ContextWork | undefined,
  fundId: string
): WorkCard | undefined {
  return work?.cards.find(
    (card) =>
      card.fund_ids.includes(fundId) &&
      card.identity.holder_id === item.scope.holder_id &&
      card.identity.instrument_id === item.scope.instrument_id &&
      card.identity.issuer_id === item.scope.issuer_id &&
      card.identity.entity_id === item.scope.entity_id
  )
}

/** The same dated decision is available without the contextual Record API. */
export function FundReportingPanel({ fundId }: { fundId: string }) {
  const [refresh, setRefresh] = useState(0)
  const [editorOpen, setEditorOpen] = useState(false)
  const schedule = useRead<ReportingSchedule>(`/api/fund-reporting/funds/${fundId}`, refresh)
  return (
    <FundReporting
      fundId={fundId}
      scope={{ kind: 'fund', fund_id: fundId }}
      schedule={schedule}
      refresh={refresh}
      onRefresh={() => setRefresh((value) => value + 1)}
      editorOpen={editorOpen}
      onEdit={() => setEditorOpen(true)}
      onCloseEditor={() => setEditorOpen(false)}
    />
  )
}

export default function FundReporting({
  fundId,
  scope,
  work,
  schedule: read,
  refresh,
  onRefresh,
  editorOpen,
  onEdit,
  onCloseEditor,
}: Props) {
  const { user } = useAuth()
  const manager = user?.role === 'manager' || user?.role === 'admin'
  const [selected, setSelected] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<'checks' | 'signoff' | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const pending = useRef(false)
  const retry = useRef<{ content: string; id: string } | null>(null)
  const schedule = read.value?.fund_id === fundId ? read.value : undefined
  /* "No reporting schedule" is a FACT the server states on a successful
   * read (schedule_missing / an empty period list) — not an error shape.
   * A schedule 404 means the FUND is unavailable to this caller
   * (admit_fund refusal) and must stay a loud error with Retry: mapping
   * every 404 to the quiet line hid real failures (Codex finding on
   * #1620, 2026-11-02). */
  const noSource = schedule != null && schedule.periods.length === 0
  const selectedId = schedule?.periods.some((period) => period.id === selected)
    ? selected
    : (schedule?.periods[0]?.id ?? null)
  const periodRead = useRead<ReportingReview>(
    selectedId ? `/api/fund-reporting/periods/${selectedId}` : null,
    refresh
  )
  const review =
    periodRead.value?.fund_id === fundId && periodRead.value.id === selectedId
      ? periodRead.value
      : undefined
  const closures = review?.manifest.closures ?? []
  const closed = closures.filter((item) => item.ready === true).length
  const canSign =
    manager &&
    review?.ready === true &&
    review.can_sign_off === true &&
    review.historical_only === false &&
    !review.signed_current &&
    review.manifest.period_revision === review.revision &&
    /^[0-9a-f]{64}$/.test(review.manifest_fingerprint)
  const canCheck =
    !!review?.as_of &&
    !review.historical_only &&
    !['needs_review', 'cancelled'].includes(review.status) &&
    !review.manifest.blockers.includes('reporting_cutoff_has_not_occurred')
  const refreshCurrent = () => {
    onRefresh()
  }
  const edit = () => {
    if (!pending.current) {
      setOpen(false)
      onEdit()
    }
  }
  const act = async (kind: 'checks' | 'signoff') => {
    if (!review || pending.current || (kind === 'signoff' ? !canSign : !canCheck)) return
    pending.current = true
    setBusy(kind)
    setNotice(null)
    try {
      if (kind === 'checks') {
        await apiFetch<ReportingReview>(`/api/fund-reporting/periods/${review.id}/checks`, {
          method: 'POST',
        })
        setNotice('Fresh checks requested. Refresh the checklist to see their results.')
      } else {
        const body = {
          period_revision: review.revision,
          manifest_fingerprint: review.manifest_fingerprint,
        }
        const content = review.id + JSON.stringify(body)
        if (retry.current?.content !== content) retry.current = { content, id: crypto.randomUUID() }
        await apiFetch<ReportingSignoff>(`/api/fund-reporting/periods/${review.id}/sign-off`, {
          method: 'POST',
          body: JSON.stringify({ ...body, idempotency_key: retry.current.id }),
        })
        setNotice('Sign-off saved. The current checklist is being refreshed.')
      }
      refreshCurrent()
    } catch (caught) {
      setNotice(failure(caught))
      if (caught instanceof ApiError && [403, 404, 409].includes(caught.status)) refreshCurrent()
    } finally {
      pending.current = false
      setBusy(null)
    }
  }
  const title = review?.as_of
    ? `As of ${formatOfficerDate(review.as_of)}`
    : 'Financial reporting cutoff not set'
  const state =
    review?.signed_current === true && !review.historical_only
      ? 'Signed off'
      : review?.historical_only
        ? 'Historical decision'
        : review?.ready === true
          ? 'Ready for manager sign-off'
          : 'Review required'
  return (
    <>
      <section className="fa-card fr-summary" data-testid="fund-reporting">
        <div className="fr-summary-line">
          <div>
            <strong>Fund reporting</strong>
            <small>{schedule?.periods.length ? title : 'Reporting dates not set'}</small>
          </div>
          <div className="fr-summary-status">
            {review ? (
              <>
                <span>
                  {closures.length
                    ? `${closed} of ${closures.length} subchains Closed for this date`
                    : 'Subchain checklist needs review'}
                </span>
                <small>{state}</small>
              </>
            ) : (
              <span>
                {read.loading || periodRead.loading
                  ? 'Loading reporting checklist…'
                  : noSource
                    ? 'Reporting dates not set'
                    : 'Current checklist unavailable'}
              </span>
            )}
          </div>
          <button
            className="nav-link"
            onClick={() => setOpen(true)}
            disabled={!schedule?.periods.length}
          >
            Review fund sign-off checklist ›
          </button>
          {schedule && !schedule.periods.length ? (
            <button className="nav-link" onClick={edit}>
              Add reporting dates ›
            </button>
          ) : null}
        </div>
        {notice && !open ? (
          <p className="fr-notice" role="status">
            {notice}
          </p>
        ) : null}
        {read.error && !noSource ? (
          <ErrorState what="reporting schedule" error={read.error} onRetry={onRefresh} compact />
        ) : null}
        {noSource ? (
          <p className="fr-notice" role="note">
            No reporting schedule on record for this fund yet.
          </p>
        ) : null}
        {periodRead.error && !open ? (
          <ErrorState
            what="reporting checklist"
            error={periodRead.error}
            onRetry={onRefresh}
            compact
          />
        ) : null}
      </section>
      {editorOpen && schedule ? (
        <ScheduleEditor
          key={schedule.schedule_revision}
          schedule={schedule}
          manager={manager}
          onClose={onCloseEditor}
          onSaved={() => {
            onCloseEditor()
            setNotice('Reporting dates saved.')
            refreshCurrent()
          }}
          onStale={(message) => {
            onCloseEditor()
            setNotice(message)
            refreshCurrent()
          }}
        />
      ) : null}
      {open ? (
        <Dialog title="Manager fund sign-off" onClose={() => setOpen(false)}>
          <label className="fr-period-select">
            Reporting period
            <select
              aria-label="Reporting period"
              value={selectedId ?? ''}
              disabled={!!busy}
              onChange={(event) => {
                setSelected(event.target.value)
                setNotice(null)
              }}
            >
              {schedule?.periods.map((period) => (
                <option key={period.id} value={period.id}>
                  {periodLabel(period)}
                </option>
              ))}
            </select>
          </label>
          {periodRead.loading ? <p role="status">Loading current checklist…</p> : null}
          {periodRead.error ? (
            <ErrorState what="reporting checklist" error={periodRead.error} onRetry={onRefresh} />
          ) : null}
          {review ? (
            <>
              <dl className="fr-period-facts">
                <div>
                  <dt>Financial cutoff</dt>
                  <dd>{review.as_of ? formatOfficerDate(review.as_of) : 'Not set'}</dd>
                </div>
                <div>
                  <dt>Report due date</dt>
                  <dd>{review.due_date ? formatOfficerDate(review.due_date) : 'Not recorded'}</dd>
                </div>
              </dl>
              <Source period={review} fundId={fundId} />
              <p
                className={
                  review.signed_current === true && !review.historical_only
                    ? 'fr-signed'
                    : 'fr-muted'
                }
              >
                {state}
              </p>
              {review.last_attention ? <p>{review.last_attention}</p> : null}
              <div className="fr-checklist">
                {closures.map((item, index) => {
                  const card = matchingCard(item, work, fundId)
                  return (
                    <div className="fr-check" key={item.closure_id ?? JSON.stringify(item.scope)}>
                      <span
                        className={item.ready === true ? 'fr-check-mark ready' : 'fr-check-mark'}
                        aria-label={
                          item.ready === true ? 'Closed for this reporting date' : 'Review needed'
                        }
                      >
                        {item.ready === true ? '✓' : '·'}
                      </span>
                      <div>
                        {card ? (
                          <Link to={subchainOverviewHref(card, scope)}>{card.label} ›</Link>
                        ) : (
                          <strong>Subchain {index + 1}</strong>
                        )}
                        <small>
                          {item.ready === true
                            ? 'Closed for this date'
                            : item.status === 'eligible'
                              ? 'Ready for officer Close'
                              : item.status === 'closed'
                                ? 'Fresh check needed'
                                : humanizeToken(item.status)}
                        </small>
                        <Blockers values={item.blockers} />
                      </div>
                    </div>
                  )
                })}
              </div>
              <Blockers values={review.manifest.blockers} />
            </>
          ) : null}
          {notice ? (
            <p role="status" className="fr-notice">
              {notice}
            </p>
          ) : null}
          <div className="fr-actions">
            <button
              type="button"
              className="fr-button"
              onClick={edit}
              disabled={!!busy || !schedule}
            >
              Edit reporting dates
            </button>
            <button
              type="button"
              className="fr-button"
              onClick={() => void act('checks')}
              disabled={!!busy || !canCheck}
            >
              {busy === 'checks' ? 'Requesting checks…' : 'Check reporting date'}
            </button>
            <button type="button" className="nav-link" onClick={refreshCurrent} disabled={!!busy}>
              Refresh checklist
            </button>
            {manager ? (
              <button
                type="button"
                className="fr-button primary"
                onClick={() => void act('signoff')}
                disabled={!!busy || !canSign}
              >
                {busy === 'signoff' ? 'Signing off…' : 'Sign off fund'}
              </button>
            ) : (
              <span className="fr-muted">A manager signs off the fund.</span>
            )}
          </div>
        </Dialog>
      ) : null}
    </>
  )
}
